import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as S from './Footer.style';

// Monochrome inline SVG icons (currentColor) keep the footer dependency-free.
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3V9zm7 0h3.8v1.7h.06c.53-.95 1.83-1.95 3.77-1.95 4.03 0 4.77 2.65 4.77 6.1V21h-4v-5.27c0-1.26-.02-2.88-1.76-2.88-1.76 0-2.03 1.37-2.03 2.79V21h-4V9z"/>
  </svg>
);

const Footer: React.FC = () => {
  const { t } = useTranslation();

  const navLinks: Array<[string, string]> = [
    ['/kaufen', t('nav.buy')],
    ['/verkaufen', t('nav.sell')],
    ['/suchanfrage', t('nav.request')],
    ['/uber-mich', t('nav.about')],
    ['/showroom', t('nav.showroom')],
    ['/kontakt', t('nav.contact')],
  ];

  const legalLinks: Array<[string, string]> = [
    ['/datenschutz', t('footer.privacy')],
    ['/impressum', t('footer.imprint')],
    ['/agb', t('footer.terms')],
    ['/widerruf', t('footer.revocation')],
    ['/versand', t('footer.shipping')],
    ['/zahlung', t('footer.payment')],
  ];

  return (
    <S.FooterContainer data-testid="site-footer">
      <S.FooterContent>
        <S.TopRow>
          <S.Brand as={Link} to="/">
            <S.Logo src="/assets/images/logo-footer.png" alt="MR - luxury watches" />
          </S.Brand>

          <S.SocialRow>
            <S.SocialIcon
              href="https://www.instagram.com/mr_luxury_watches_germany"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="footer-social-instagram"
            >
              <InstagramIcon />
            </S.SocialIcon>
            <S.SocialIcon
              href="https://www.linkedin.com/in/michael-ruckes-722b81120/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              data-testid="footer-social-linkedin"
            >
              <LinkedInIcon />
            </S.SocialIcon>
          </S.SocialRow>
        </S.TopRow>

        <S.Divider />

        <S.LinkSection data-testid="footer-nav-row">
          <S.LinkGroup>
            {navLinks.map(([href, label]) => (
              <S.FooterLink as={Link} to={href} key={href}>{label}</S.FooterLink>
            ))}
          </S.LinkGroup>
        </S.LinkSection>

        <S.LinkSection data-testid="footer-legal-row">
          <S.LinkGroup>
            {legalLinks.map(([href, label]) => (
              <S.LegalLink as={Link} to={href} key={href}>{label}</S.LegalLink>
            ))}
            <S.LegalLink
              as="button"
              onClick={() => window.dispatchEvent(new Event('mrlw:openCookieBanner'))}
              data-testid="footer-cookie-settings"
            >
              {t('footer.cookie_settings')}
            </S.LegalLink>
          </S.LinkGroup>
        </S.LinkSection>

        <S.FooterBottom>
          © {new Date().getFullYear()} MR - luxury watches, Michael Ruckes. {t('footer.copyright')}
        </S.FooterBottom>
      </S.FooterContent>
    </S.FooterContainer>
  );
};

export default Footer;
